import React, { Fragment, useEffect, useRef, useState } from "react"

import { focusElement } from "utils/focusElement"

import {
  Container,
  Input,
  Form,
  Label,
  HeaderContainer,
  CloseIcon,
  CloseIconButton,
  NoteInput,
  Error,
  SmallLabel,
  ButtonContainer,
  Title,
  Success,
} from "./styles"

const PopUpForm = ({
  title,
  ctaText,
  optionalFields,
  requiredFields,
  submitPrimary,
  hasError,
  successMessageOverride,
  elRef,
  dataId,
  hideOnPageLoad,
  onClose,
  submitting,
  submitted,
  onSubmit,
  ...props
}) => {
  const submitRef = useRef(null)
  const formHeight = useRef(null)
  const closeIconRef = useRef(null)
  const firstName = useRef("")
  const lastName = useRef("")
  const note = useRef("")
  const email = useRef("")
  const [emptyFieldError, setEmptyFieldError] = useState(false)
  const [emptyObj, setEmptyObj] = useState({
    firstName: false,
    lastName: false,
    email: false,
    note: false,
  })

  let errorMessage
  if (emptyFieldError) {
    errorMessage = "Please fill in all the fields."
  } else if (hasError) {
    errorMessage =
      "We are sorry - an error has occurred in submitting your request, but you can email mbgcoaching@mindbodygreen.com to contact us directly with your question."
  }

  const submitHandler = async (e) => {
    e.preventDefault()
    const payload = {
      firstName: firstName.current,
      lastName: lastName.current,
      note: note.current,
      email: email.current,
    }

    if (Object.values(payload).includes("")) {
      setEmptyFieldError(true)
      setEmptyObj({
        firstName: !firstName.current,
        lastName: !lastName.current,
        email: !email.current,
        note: !note.current,
      })
    } else {
      setEmptyFieldError(false)
      setEmptyObj({
        firstName: !firstName.current,
        lastName: !lastName.current,
        email: !email.current,
        note: !note.current,
      })
      onSubmit({
        email: email.current,
        firstName: firstName.current,
        lastName: lastName.current,
        note: note.current,
      })
    }
  }

  const firstNameCheck =
    optionalFields.includes("First Name") || requiredFields.includes("First Name")
  const lastNameCheck = optionalFields.includes("Last Name") || requiredFields.includes("Last Name")
  const emailCheck = optionalFields.includes("Email") || requiredFields.includes("Email")

  const handleClose = (e) => {
    e.preventDefault()
    if (e.keyCode === 13 || e.keyCode === 32) {
      onClose()
    }
  }

  useEffect(() => {
    if (document.querySelector("#contact-form")) {
      formHeight.current = document.querySelector("#contact-form").offsetHeight
    }

    if (document.querySelector("#submit-input-firstName")) {
      document.querySelector("#submit-input-firstName").focus()
    }

    const handler = (e) => {
      if (e.key === "Tab" && submitRef && e.shiftKey === true) {
        if (e.target.id === closeIconRef.current.id) {
          const submitButtonId = `#${submitRef.current.id}`
          focusElement(e, submitButtonId)
        }
      } else if (e.key === "Tab" && closeIconRef && e.shiftKey === false) {
        if (e.target.id === submitRef.current.id) {
          const closeButtonId = `#${closeIconRef.current.id}`
          focusElement(e, closeButtonId)
        }
      }
    }
    document.addEventListener("keydown", handler)

    return () => {
      document.removeEventListener("keydown", handler)
    }
  }, [])

  const handleChange = (e) => {
    const refObject = {
      firstName,
      lastName,
      note,
      email,
    }
    const { name, value } = e.target
    refObject[name].current = value
  }

  return (
    <Container hideOnPageLoad={hideOnPageLoad} data-exp-id={dataId} ref={elRef} {...props}>
      <HeaderContainer>
        {title && <Title>{title}</Title>}
        <CloseIconButton
          onClick={onClose}
          tabIndex="0"
          ref={closeIconRef}
          id="contact-close-icon"
          onKeyUp={handleClose}
        >
          <CloseIcon />
        </CloseIconButton>
      </HeaderContainer>
      {!submitted && (
        <Form
          onSubmit={submitHandler}
          id="contact-form"
          formHeight={formHeight.current}
          displayError={emptyFieldError || hasError}
        >
          {firstNameCheck && (
            <Fragment key="First Name">
              <Label htmlFor="submit-input-firstName">First Name</Label>
              <Input
                id="submit-input-firstName"
                name="firstName"
                onChange={handleChange}
                empty={emptyObj.firstName}
              />
            </Fragment>
          )}
          {lastNameCheck && (
            <Fragment key="Last Name">
              <Label htmlFor="submit-input-lastName">Last Name</Label>
              <Input
                id="submit-input-lastName"
                name="lastName"
                onChange={handleChange}
                empty={emptyObj.lastName}
              />
            </Fragment>
          )}
          {emailCheck && (
            <Fragment key="Email">
              <Label htmlFor="submit-input-email">Email address</Label>
              <Input
                id="submit-input-email"
                name="email"
                onChange={handleChange}
                empty={emptyObj.email}
              />
            </Fragment>
          )}
          <Fragment key="Help">
            <Label htmlFor="submit-input-help">
              How can we help you?<SmallLabel>(limit 250 characters)</SmallLabel>
            </Label>
            <NoteInput
              id="submit-input-help"
              name="note"
              note
              onChange={handleChange}
              empty={emptyObj.note}
              maxLength="250"
            />
          </Fragment>
          <ButtonContainer>
            <Input
              type="submit"
              value={submitting ? "SENDING..." : ctaText}
              onChange={() => null}
              disabled={!!submitting}
              primary={submitPrimary}
              ref={submitRef}
              id="contact-submit-button"
            />
          </ButtonContainer>
          {(emptyFieldError || hasError) && <Error>{errorMessage}</Error>}
        </Form>
      )}
      {submitted && (
        <Success height={formHeight.current} dangerouslySetInnerHTML={{ __html: submitted }} />
      )}
    </Container>
  )
}

export default PopUpForm
